import { useState } from 'react';
import axios from 'axios';
import { MapPin, Pencil, Save, X, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const FIELDS = ['adresse', 'code_postal', 'ville'];
export const isAdresseComplete = (p) => FIELDS.every(k => String(p?.[k] || '').trim());

const AdresseCandidatCard = ({ candidatId, profil, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const complete = isAdresseComplete(profil);

  const startEdit = () => {
    setForm({ adresse: profil.adresse || '', code_postal: profil.code_postal || '', ville: profil.ville || '' });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!isAdresseComplete(form)) {
      toast.error("L'adresse, le code postal et la ville sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`/candidats/${candidatId}/adresse`, form);
      toast.success('Adresse mise à jour');
      setEditing(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement de l'adresse");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={complete ? '' : 'border-amber-300'} data-testid="candidat-adresse-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#8DC63F]" />
            Adresse
          </span>
          {!editing && (
            <Button variant="ghost" size="sm" onClick={startEdit} className="text-slate-600" data-testid="btn-edit-adresse">
              <Pencil className="h-4 w-4 mr-1" />
              Modifier l'adresse
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        {editing ? (
          <div className="space-y-3" data-testid="adresse-edit-form">
            <div className="space-y-1">
              <Label>Adresse (numéro et rue) *</Label>
              <Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} data-testid="adresse-edit-adresse" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label>Code postal *</Label>
                <Input value={form.code_postal} maxLength={5} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} data-testid="adresse-edit-code-postal" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Ville *</Label>
                <Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} data-testid="adresse-edit-ville" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving} data-testid="btn-cancel-adresse">
                <X className="h-4 w-4 mr-1" />
                Annuler
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#8DC63F] hover:bg-[#7db336]" data-testid="btn-save-adresse">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Enregistrer
              </Button>
            </div>
          </div>
        ) : complete ? (
          <div data-testid="adresse-display">
            <p className="font-medium">{profil.adresse}</p>
            <p className="text-slate-600">{profil.code_postal} {profil.ville}</p>
          </div>
        ) : (
          <p className="flex items-start gap-2 text-amber-700" data-testid="adresse-incomplete-warning">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            Adresse incomplète : obligatoire pour la convention de formation.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default AdresseCandidatCard;
